import React, { useMemo, useState } from "react";
import { FirebaseError } from "firebase/app";
import { signInWithEmailAndPassword } from "firebase/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { OutlinedPanel, Button, Input, FormField } from "../../design-system";
import { auth } from "../../firebase/firebase";
import { Footer } from "../../components";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [touched, setTouched] = useState({ email: false, password: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authError, setAuthError] = useState("");

  const redirectTo =
    (location.state as { from?: { pathname?: string } } | null)?.from
      ?.pathname || "/playlist";

  const isEmailValid = useMemo(() => EMAIL_REGEX.test(email.trim()), [email]);
  const isPasswordValid = useMemo(() => password.length > 6, [password]);
  const isFormValid = isEmailValid && isPasswordValid;

  const emailError =
    touched.email && !email.trim()
      ? "E-mail é obrigatório."
      : touched.email && !isEmailValid
        ? "Digite um endereço de e-mail válido."
        : "";

  const passwordError =
    touched.password && !password
      ? "Senha é obrigatória."
      : touched.password && !isPasswordValid
        ? "A senha precisa ter mais de 6 caracteres."
        : "";

  const getAuthErrorMessage = (errorCode: string) => {
    switch (errorCode) {
      case "auth/invalid-credential":
      case "auth/user-not-found":
      case "auth/wrong-password":
        return "E-mail ou senha inválidos.";
      case "auth/too-many-requests":
        return "Muitas tentativas. Aguarde e tente novamente.";
      case "auth/network-request-failed":
        return "Erro de rede. Verifique sua conexão e tente novamente.";
      default:
        return "Não foi possível entrar agora. Tente novamente.";
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setTouched({ email: true, password: true });
    setAuthError("");

    if (!isFormValid) {
      return;
    }

    setIsSubmitting(true);

    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      navigate(redirectTo, { replace: true });
    } catch (error) {
      if (error instanceof FirebaseError) {
        setAuthError(getAuthErrorMessage(error.code));
      } else {
        setAuthError("Não foi possível entrar agora. Tente novamente.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-6 bg-gradient-to-r from-auth-start to-auth-end p-6">
      <OutlinedPanel tone="neutral" title="Vismal - Acesso Restrito">
        <form className="flex flex-col gap-3.5" onSubmit={handleSubmit} noValidate>
          <FormField id="email" label="E-mail" error={emailError}>
            <Input
              id="email"
              type="email"
              name="email"
              hasError={Boolean(emailError)}
              aria-describedby={emailError ? "email-error" : undefined}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
              placeholder="voce@exemplo.com"
              autoComplete="email"
            />
          </FormField>

          <FormField id="password" label="Senha" error={passwordError}>
            <Input
              id="password"
              type="password"
              name="password"
              hasError={Boolean(passwordError)}
              aria-describedby={passwordError ? "password-error" : undefined}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
              placeholder="Digite sua senha"
              autoComplete="current-password"
            />
          </FormField>

          <Button variant="secondary" className="mt-2" type="submit" disabled={!isFormValid || isSubmitting}>
            {isSubmitting ? "Entrando..." : "Entrar"}
          </Button>

          {authError && <p role="alert" className="mt-0.5 text-feedback text-danger-feedback">{authError}</p>}
        </form>
      </OutlinedPanel>

      <Footer />
    </main>
  );
};

export default Login;
